import React from "react";
import { cn } from "@/lib/utils";

/**
 * Minimal, safe renderer for model replies: headings (#), bullets (-, *, •), numbered lists, **bold**, paragraphs.
 * Renders React elements only (no HTML injection).
 */
const inline = (text, keyPrefix) => {
  const parts = String(text).split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return parts.map((p, i) => (p.startsWith("**") && p.endsWith("**") ? <strong key={`${keyPrefix}-${i}`} className="font-semibold text-foreground">{p.slice(2, -2)}</strong> : <React.Fragment key={`${keyPrefix}-${i}`}>{p}</React.Fragment>));
};

export const LightMarkdown = ({ text, className = "", testId }) => {
  if (!text) return null;
  const lines = String(text).replace(/\r/g, "").split("\n");
  const blocks = [];
  let list = null;
  const flush = () => {
    if (list) {
      blocks.push(list);
      list = null;
    }
  };
  lines.forEach((raw, idx) => {
    const line = raw.trimEnd();
    if (!line.trim()) {
      flush();
      return;
    }
    const h = line.match(/^(#{1,4})\s+(.*)$/);
    if (h) {
      flush();
      blocks.push({ type: "h", level: h[1].length, text: h[2], key: idx });
      return;
    }
    const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
    const numbered = line.match(/^\s*(\d+)[.)]\s+(.*)$/);
    if (bullet || numbered) {
      const type = bullet ? "ul" : "ol";
      if (!list || list.type !== type) {
        flush();
        list = { type, items: [], key: idx };
      }
      list.items.push(bullet ? bullet[1] : numbered[2]);
      return;
    }
    flush();
    blocks.push({ type: "p", text: line, key: idx });
  });
  flush();

  return (
    <div className={cn("space-y-2 text-sm leading-6", className)} data-testid={testId}>
      {blocks.map((b) => {
        if (b.type === "h") return <div key={b.key} className={cn("font-heading font-semibold text-foreground", b.level <= 2 ? "text-sm md:text-base" : "text-sm")}>{inline(b.text, b.key)}</div>;
        if (b.type === "ul") return <ul key={b.key} className="list-disc space-y-1 pl-5">{b.items.map((it, i) => <li key={i}>{inline(it, `${b.key}-${i}`)}</li>)}</ul>;
        if (b.type === "ol") return <ol key={b.key} className="list-decimal space-y-1 pl-5">{b.items.map((it, i) => <li key={i}>{inline(it, `${b.key}-${i}`)}</li>)}</ol>;
        return <p key={b.key} className="whitespace-pre-wrap">{inline(b.text, b.key)}</p>;
      })}
    </div>
  );
};
